import { forwardRef, useRef, useState } from 'react';
import useResizeObserver from '../../Hooks/UseResizeObserver';
import { combineClassNames } from '../../Utils/ClassnameUtils';

export type GWPSegmentedControlOption<T extends string | number> = {
	value: T;
	label: React.ReactNode;
	disabled?: boolean;
};

export interface GWPSegmentedControlProps<T extends string | number>
	extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'> {
	options: ReadonlyArray<GWPSegmentedControlOption<T>>;
	value: T;
	onValueChange: (value: T) => void;
	size?: 'sm' | 'md' | 'lg';
	tone?: 'neutral' | 'primary';
	fullWidth?: boolean;
}

type GWPSegmentedControlComponent = <T extends string | number>(
	props: GWPSegmentedControlProps<T> & React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null;

const GWPSegmentedControlInner = forwardRef(function GWPSegmentedControlInner<T extends string | number>(
	props: GWPSegmentedControlProps<T>,
	ref: React.ForwardedRef<HTMLDivElement>,
) {
	const {
		className,
		options,
		value,
		onValueChange,
		size = 'md',
		tone = 'neutral',
		fullWidth = false,
		...rest
	} = props;

	const containerRef = useRef<HTMLDivElement | null>(null);
	const interactiveButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const [indicatorNode, setIndicatorNode] = useState<HTMLButtonElement | null>(null);

	useResizeObserver({
		ref: containerRef,
		callback: () => setIndicatorNode(null),
	});

	const selectedIndex = options.findIndex((option) => option.value === value);
	const firstEnabledIndex = options.findIndex((option) => !option.disabled);
	const lastEnabledIndex = [...options].reduce(
		(lastIndex, option, index) => (!option.disabled ? index : lastIndex),
		-1,
	);
	const fallbackTabIndex =
		selectedIndex >= 0 && !options[selectedIndex]?.disabled ? selectedIndex : firstEnabledIndex;

	const setContainerNode = (node: HTMLDivElement | null) => {
		containerRef.current = node;

		if (typeof ref === 'function') {
			ref(node);
			return;
		}

		if (ref !== null) {
			ref.current = node;
		}
	};

	const focusAndSelectIndex = (index: number) => {
		const option = options[index];
		const button = interactiveButtonRefs.current[index];

		if (!option || option.disabled || !button) {
			return;
		}

		button.focus();
		onValueChange(option.value);
	};

	const moveSelection = (currentIndex: number, direction: 1 | -1) => {
		if (options.length === 0) {
			return;
		}

		let nextIndex = currentIndex;
		for (let attempts = 0; attempts < options.length; attempts += 1) {
			nextIndex = (nextIndex + direction + options.length) % options.length;
			if (!options[nextIndex]?.disabled) {
				focusAndSelectIndex(nextIndex);
				return;
			}
		}
	};

	const rootClassName = combineClassNames(
		'gwp-segmented-control',
		size,
		tone,
		fullWidth ? 'full-width' : undefined,
		className,
	);

	// Layout-space measurements stay accurate when an ancestor is visually scaled, such as an opening modal.
	const indicatorStyle =
		indicatorNode !== null
			? {
					width: indicatorNode.offsetWidth,
					height: indicatorNode.offsetHeight,
					transform: `translateX(${indicatorNode.offsetLeft}px)`,
				}
			: undefined;

	return (
		<div ref={setContainerNode} className={rootClassName} role="radiogroup" aria-orientation="horizontal" {...rest}>
			<div className="gwp-segmented-underlay" aria-hidden="true">
				{options.map((option) => {
					const isSelected = option.value === value;
					const buttonClassName = combineClassNames(
						'gwp-segment',
						isSelected ? 'selected' : undefined,
						option.disabled ? 'disabled' : undefined,
					);

					return (
						<button
							key={String(option.value)}
							type="button"
							tabIndex={-1}
							className={buttonClassName}
							disabled={option.disabled}
							ref={(node) => {
								if (isSelected && node !== null) {
									setIndicatorNode(node);
								}
							}}
						>
							<span className="gwp-segment-label">{option.label}</span>
						</button>
					);
				})}
			</div>

			{indicatorStyle !== undefined && <div className="gwp-segmented-indicator" style={indicatorStyle} />}

			<div className="gwp-segmented-overlay">
				{options.map((option, index) => {
					const isSelected = option.value === value;
					const buttonClassName = combineClassNames(
						'gwp-segment',
						isSelected ? 'selected' : undefined,
						option.disabled ? 'disabled' : undefined,
					);

					return (
						<button
							key={String(option.value)}
							ref={(node) => {
								interactiveButtonRefs.current[index] = node;
							}}
							type="button"
							role="radio"
							className={buttonClassName}
							aria-checked={isSelected}
							disabled={option.disabled}
							tabIndex={index === fallbackTabIndex ? 0 : -1}
							onClick={() => onValueChange(option.value)}
							onKeyDown={(event) => {
								switch (event.key) {
									case 'ArrowRight':
									case 'ArrowDown':
										event.preventDefault();
										moveSelection(index, 1);
										break;
									case 'ArrowLeft':
									case 'ArrowUp':
										event.preventDefault();
										moveSelection(index, -1);
										break;
									case 'Home':
										event.preventDefault();
										focusAndSelectIndex(firstEnabledIndex);
										break;
									case 'End':
										event.preventDefault();
										focusAndSelectIndex(lastEnabledIndex);
										break;
								}
							}}
						>
							<span className="gwp-segment-label">{option.label}</span>
						</button>
					);
				})}
			</div>
		</div>
	);
});

GWPSegmentedControlInner.displayName = 'GWPSegmentedControl';

const GWPSegmentedControl = GWPSegmentedControlInner as GWPSegmentedControlComponent;

export default GWPSegmentedControl;
